import { useState } from "react";
import { useMutation } from "@apollo/client";
import { useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { RESET_PASSWORD } from "../../../queries/auth.queries";
import {
  ResetPasswordResponse,
  ResetPasswordVariables,
} from "./reset-password.types";

export interface ResetPasswordFormValues {
  newPassword: string;
}

interface SnackState {
  open: boolean;
  success: boolean;
  message: string;
}

export const useResetPassword = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const token = new URLSearchParams(location.search).get("token");

  const [snack, setSnack] = useState<SnackState>({
    open: false,
    success: false,
    message: "",
  });

  const [reset] = useMutation<ResetPasswordResponse, ResetPasswordVariables>(
    RESET_PASSWORD
  );

  const closeSnack = () => setSnack((prev) => ({ ...prev, open: false }));

  const showSnack = (success: boolean, message: string) => {
    setSnack({ open: true, success, message });
  };

  const onSubmit = async ({ newPassword }: ResetPasswordFormValues) => {
    if (!token) {
      showSnack(false, t("auth.invalidResetLink"));
      return;
    }

    try {
      const result = await reset({
        variables: { token, password: newPassword },
      });
      const resetPasswordResult = result?.data?.resetPassword;

      if (resetPasswordResult?.__typename === "CurrentUser") {
        showSnack(true, t("auth.passwordChanged"));
        setTimeout(() => {
          history.push("/home");
        }, 2000);
        return;
      }

      showSnack(false, resetPasswordResult?.message ?? t("errors.generic"));
    } catch (err) {
      showSnack(false, (err as Error)?.message || t("errors.generic"));
    }
  };

  return {
    onSubmit,
    snack,
    closeSnack,
  };
};
